/**
 * MeasurementsTable - Historical measurements table component
 * Displays measurements data with edit/delete actions
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { Meranie } from "../types";
import { formatDate } from "../utils";

interface MeasurementsTableProps {
  merania: Meranie[];
  isLoading: boolean;
  onEdit: (meranie: Meranie) => void;
  onDelete: (id: number) => void;
}

export function MeasurementsTable({
  merania,
  isLoading,
  onEdit,
  onDelete,
}: MeasurementsTableProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("measurements:measurementHistory")}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("common:loading")}
          </div>
        ) : merania.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t("measurements:noMeasurements")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("measurements:table.date")}</TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.weight")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.bodyFat")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.muscle")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.visceralFat")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.bmr")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("measurements:table.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {merania.map(meranie => (
                  <TableRow key={meranie.id}>
                    <TableCell className="font-medium">
                      {formatDate(meranie.datum)}
                    </TableCell>
                    <TableCell className="text-right">
                      {meranie.vahaKg || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {meranie.tukPercent || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {meranie.svalovinaPercent || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {meranie.visceralnytuk || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {meranie.bazalnyMetabolizmus || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(meranie)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(meranie.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
